import { Box, Button, Chip, TextField } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { financeApi } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { nextTagPaletteColor } from "../lib/tagColors";
import type { Group, Tag } from "../lib/types";
import { PanelActions, PanelHeader } from "./Panel";
import { SidePanel } from "./SidePanel";

/**
 * Nested side panel for picking record tags. Selection is a local draft:
 * "Done" applies it to the record form, the X button abandons the whole
 * panel stack (picker and record form) without applying anything, and
 * dismissing via backdrop/Escape drops only the draft.
 */
export function TagPickerPanel({
  group,
  categoryId,
  selectedTagIds,
  onApply,
  onClose,
  onCancelAll,
}: {
  group: Group;
  categoryId: string;
  selectedTagIds: string[];
  onApply: (tagIds: string[]) => void;
  onClose: () => void;
  onCancelAll: () => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [draftIds, setDraftIds] = useState<string[]>(selectedTagIds);

  const tagsQuery = useQuery({ queryKey: queryKeys.tags(group.id), queryFn: () => financeApi.tags(group.id) });
  const popularQuery = useQuery({
    queryKey: queryKeys.popularTags(group.id, categoryId),
    queryFn: () => financeApi.popularTags(group.id, categoryId),
    enabled: Boolean(categoryId),
  });

  const tags = tagsQuery.data || [];
  const activeTags = useMemo(() => tags.filter((tag) => !tag.is_archived), [tags]);
  const searchText = search.trim().toLowerCase();
  const visibleTags = searchText ? activeTags.filter((tag) => tag.name.toLowerCase().includes(searchText)) : activeTags;
  const popularTags = (popularQuery.data || []).filter((tag) => !searchText || tag.name.toLowerCase().includes(searchText)).slice(0, 5);
  const hasExactMatch = activeTags.some((tag) => tag.name.toLowerCase() === searchText);
  const canCreate = Boolean(searchText) && !hasExactMatch;

  const toggleDraft = (tagId: string) => {
    setDraftIds((current) => (current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId]));
  };

  const createMutation = useMutation({
    mutationFn: (name: string) => financeApi.createTag(group.id, { name, color: nextTagPaletteColor(activeTags.length) }),
    onSuccess: async (tag) => {
      setSearch("");
      setStatus("");
      await queryClient.invalidateQueries({ queryKey: queryKeys.tags(group.id) });
      toggleDraft(tag.id);
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const renderTagChip = (tag: Tag) => {
    const selected = draftIds.includes(tag.id);
    return (
      <Chip
        key={tag.id}
        className="chip tag-picker-chip"
        clickable
        icon={selected ? <CheckIcon fontSize="small" style={{ color: "#fff" }} /> : undefined}
        label={tag.name}
        variant={selected ? "filled" : "outlined"}
        style={selected ? { backgroundColor: tag.color, color: "#fff" } : { borderColor: tag.color, color: tag.color }}
        onClick={() => toggleDraft(tag.id)}
      />
    );
  };

  return (
    <SidePanel ariaLabelledBy="tagPickerTitle" className="tag-picker-panel" onClose={onClose}>
      {({ close }) => (
        <>
          <PanelHeader titleId="tagPickerTitle" title="Select tags" closeAriaLabel="Discard and close" onClose={onCancelAll} />
          <Box className="tag-picker-body">
            <TextField
              size="small"
              placeholder="Search or create a tag"
              value={search}
              autoFocus
              slotProps={{
                htmlInput: { maxLength: 60 },
                input: { startAdornment: <SearchIcon className="tag-picker-search-icon" fontSize="small" /> },
              }}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && canCreate) {
                  event.preventDefault();
                  createMutation.mutate(search.trim());
                }
              }}
            />
            {popularTags.length ? (
              <Box className="tag-picker-section">
                <span className="tag-picker-section-label">Popular for this category</span>
                <Box className="tag-picker-chips">{popularTags.map(renderTagChip)}</Box>
              </Box>
            ) : null}
            <Box className="tag-picker-section">
              <span className="tag-picker-section-label">All tags</span>
              <Box className="tag-picker-chips">
                {visibleTags.map(renderTagChip)}
                {!tagsQuery.isLoading && !visibleTags.length && !canCreate ? <p className="users-group-empty">No tags yet.</p> : null}
              </Box>
            </Box>
            {canCreate ? (
              <Button variant="outlined" size="small" type="button" disabled={createMutation.isPending} onClick={() => createMutation.mutate(search.trim())}>
                Create "{search.trim()}"
              </Button>
            ) : null}
            {status ? <p className="status error">{status}</p> : null}
          </Box>
          <PanelActions>
            <Button
              variant="contained"
              type="button"
              onClick={() => {
                onApply(draftIds);
                close();
              }}
            >
              Done
            </Button>
          </PanelActions>
        </>
      )}
    </SidePanel>
  );
}
